/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useEffect, useRef, useState } from "react";
import DictionaryTableItem from "layout/dictionary/DictionaryTableItem";
import dictionaryIcon from "icons/dictionary/dictionary.svg";
import { useSelector } from "react-redux";
import { selectDictionaries } from "slices/dictionary/selectors";
import {
  sortDictionariesByFoldersAndDocuments,
  sortObjectsByBooleanField,
  sortObjectsByDateField,
  sortObjectsByTextField,
} from "utils/dictionary";
import { sortingKeys, sortingModes } from "constants/dictionary";
import ResizableContainer from "components/ResizableContainer";
import { ReactComponent as ArrowDown } from "icons/dictionary/ArrowDown.svg";
import { ReactComponent as ArrowUp } from "icons/dictionary/ArrowUp.svg";
import InfiniteGrid from "components/Grid";
import Ellipsis from "components/Ellipsis";

const DictionaryTable = () => {
  const dictionaries = useSelector(selectDictionaries);
  const [sortedDictionaries, setSortedDictionaries] = useState(dictionaries);
  const [sortedBy, setSortedBy] = useState(sortingKeys.name);
  const [sortingMode, setSortingMode] = useState(sortingModes.ascending);
  const [columnWidths, setColumnWidths] = useState([
    "auto",
    "auto",
    100,
    100,
    80,
    165,
    94,
  ]);
  const isFirstSorting = useRef(true);
  const tableRef = useRef();
  const headerRef = useRef();
  const bodyRef = useRef();

  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.borderBoxSize[0].inlineSize !== 0) {
          const columns = Array.from(
            document.getElementsByClassName("resizable_container")
          );
          setColumnWidths(columns.map((column) => column.clientWidth));
          columns[0].style.flexGrow = 0;
          columns[1].style.flexGrow = 0;
          ro.unobserve(headerRef.current);
        }
      });
    });

    if (headerRef?.current) {
      ro.observe(headerRef.current);
    }
  }, []);

  useEffect(() => {
    setSortedDictionaries(dictionaries);
  }, [dictionaries]);

  const headerConfig = [
    {
      title: `Dictionaries (${dictionaries.length})`,
      sortBy: sortingKeys.name,
      minWidth: 40,
    },
    {
      title: "Created By",
      sortBy: sortingKeys.createdBy,
      minWidth: 40,
    },
    {
      title: "Created On",
      sortBy: sortingKeys.createdOn,
      minWidth: 40,
    },
    {
      title: "Last Updated",
      sortBy: sortingKeys.lastUpdated,
      minWidth: 40,
    },
    {
      title: "In Use",
      sortBy: sortingKeys.inUse,
      minWidth: 40,
    },
    {
      title: "Used By",
      sortBy: sortingKeys.usedBy,
      minWidth: 40,
    },
    {
      title: "Last Used",
      sortBy: sortingKeys.lastUsed,
      minWidth: 40,
    },
  ];

  useEffect(() => {
    // We are receiving sorted data from backend, so no need to sort it again
    if (isFirstSorting.current) {
      isFirstSorting.current = false;
      return;
    }

    const ascending = sortingMode === sortingModes.ascending;

    switch (sortedBy) {
      case sortingKeys.name:
        setSortedDictionaries(
          sortObjectsByTextField([...dictionaries], sortingKeys.name, ascending)
        );
        break;
      case sortingKeys.createdBy:
        setSortedDictionaries(
          sortObjectsByTextField(
            [...dictionaries].map((dictionary) => {
              if (!dictionary.createdBy) {
                return {
                  ...dictionary,
                  createdBy: "System",
                };
              }
              return dictionary;
            }),
            sortingKeys.createdBy,
            ascending
          )
        );
        break;
      case sortingKeys.createdOn:
        setSortedDictionaries(
          sortObjectsByDateField(
            [...dictionaries],
            sortingKeys.createdOn,
            ascending
          )
        );
        break;
      case sortingKeys.lastUpdated:
        setSortedDictionaries(
          sortObjectsByDateField(
            [...dictionaries],
            sortingKeys.lastUpdated,
            ascending
          )
        );
        break;
      case sortingKeys.inUse:
        setSortedDictionaries(
          sortObjectsByBooleanField(
            [...dictionaries],
            sortingKeys.inUse,
            ascending
          )
        );
        break;
      case sortingKeys.usedBy:
        setSortedDictionaries(
          sortDictionariesByFoldersAndDocuments([...dictionaries], ascending)
        );
        break;
      case sortingKeys.lastUsed:
        setSortedDictionaries(
          sortObjectsByDateField(
            [...dictionaries],
            sortingKeys.lastUsed,
            ascending
          )
        );
        break;
      default:
        break;
    }
  }, [sortedBy, sortingMode]);

  const onSortClick = (sortBy) => {
    if (sortBy === sortedBy) {
      if (sortingMode === sortingModes.ascending) {
        setSortingMode(sortingModes.descending);
      } else {
        setSortingMode(sortingModes.ascending);
      }
    } else {
      setSortedBy(sortBy);
      setSortingMode(sortingModes.ascending);
    }
  };

  const handleColumnResize = (index, newWidth) => {
    setColumnWidths(
      columnWidths.map((columnWidth, columnIndex) => {
        if (index === columnIndex) {
          return newWidth;
        }
        return columnWidth;
      })
    );
  };

  const renderSortIcon = (sortBy) => {
    if (sortBy === sortedBy) {
      if (sortingMode === sortingModes.ascending) {
        return <ArrowDown />;
      }
      if (sortingMode === sortingModes.descending) {
        return <ArrowUp />;
      }
    }
    return <ArrowDown />;
  };

  const renderRow = ({ rowIndex, style }) => (
    <div style={style} className="item">
      <DictionaryTableItem
        item={sortedDictionaries[rowIndex]}
        columnWidths={columnWidths}
      />
    </div>
  );

  const handleScroll = (scrollLeft) => {
    if (headerRef.current) {
      headerRef.current.scrollLeft = scrollLeft;
    }
  };

  return (
    <div ref={tableRef} className="dictionary__table">
      <div ref={headerRef} className="dictionary__table__header">
        <img src={dictionaryIcon} alt="dictionary" />
        {headerConfig.map(({ title, sortBy, minWidth }, index) => (
          <ResizableContainer
            index={index}
            width={columnWidths[index]}
            minWidth={minWidth}
            disableResizing={index === headerConfig.length - 1}
            onResize={handleColumnResize}
            className="dictionary__table__header__container"
          >
            <span
              className="dictionary__table__header__container__content"
              onClick={() => onSortClick(sortBy)}
            >
              <Ellipsis text={title} />
              <button
                type="button"
                className={`sort_button${sortBy === sortedBy ? " active" : ""}`}
                data-testid={sortBy}
              >
                {renderSortIcon(sortBy)}
              </button>
            </span>
          </ResizableContainer>
        ))}
      </div>
      <div className="dictionary__table__body">
        <InfiniteGrid
          outerRef={bodyRef}
          rowSize={26}
          rowCount={sortedDictionaries.length}
          renderRow={renderRow}
          columnCount={1}
          onScroll={(scrollLeft) => handleScroll(scrollLeft)}
        />
      </div>
    </div>
  );
};

export default DictionaryTable;
